/** CSV evidence export (MERGE-PLAN §4.4) — one row per control, flattened. */
import { FRAMEWORK_LABELS, type FrameworkId } from '../../types';
import type { EvidencePack } from '../types';
import { dateStamp, downloadBlob, slugify } from './download';

const FRAMEWORK_COLUMNS: readonly FrameworkId[] = [
  'cis',
  'nist',
  'iso',
  'soc2',
  'hipaa',
  'secScore',
];

function esc(v: string): string {
  // RFC-4180: wrap in quotes and double any embedded quotes when needed.
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function evidenceToCsv(pack: EvidencePack): string {
  const header = [
    'Control ID',
    'Objective',
    'Module',
    'Severity',
    'Status',
    'Affected count',
    'Affected objects',
    'Endpoints',
    'Collected at',
    ...FRAMEWORK_COLUMNS.map((f) => FRAMEWORK_LABELS[f]),
    'Remediation',
  ];

  const rows = pack.records.map((r) => {
    const affected = r.affected
      .map((a) => `${a.label}${a.detail ? ` (${a.detail})` : ''}`)
      .join('; ');
    return [
      r.controlId,
      r.objective,
      r.module,
      r.severity,
      r.status,
      String(r.affected.length),
      affected,
      r.provenance.endpoints.join('; '),
      r.provenance.collectedAt,
      ...FRAMEWORK_COLUMNS.map((f) => r.frameworks[f].join(' ')),
      r.remediation,
    ].map(esc);
  });

  return [header.map(esc).join(','), ...rows.map((row) => row.join(','))].join('\r\n');
}

export function downloadEvidenceCsv(pack: EvidencePack): void {
  const name = `evidence-${slugify(pack.meta.tenantName)}-${dateStamp(pack.meta.collectedAt)}.csv`;
  // BOM (U+FEFF) so Excel opens UTF-8 correctly.
  downloadBlob(name, 'text/csv;charset=utf-8', '﻿' + evidenceToCsv(pack));
}
